import type { ConsistencyStep, ProofStep } from '../../core/merkle/proof';
import { shortHex } from '../format';
import { Badge } from './Badge';
import { Hash } from './Hash';

/**
 * Draws hashing steps as a ladder from the leaf up to the root: each row shows the two inputs in
 * tree order (left ‖ right) and the resulting parent hash. Failed comparisons are marked in red.
 */
export function InclusionPath({ leafHash, steps, computedRoot, expectedRoot, ok }: { leafHash: Uint8Array; steps: ProofStep[]; computedRoot?: Uint8Array; expectedRoot?: Uint8Array; ok: boolean }) {
  return (
    <ol class="merkle">
      <li class="merkle-row">
        <span class="merkle-level">leaf</span>
        <span class="node node-leaf" title={leafHash.toHex()}>
          {shortHex(leafHash, 6)}
        </span>
        <span class="muted small">= SHA-256(0x00 ‖ leaf input)</span>
      </li>
      {steps.map((s, i) => (
        <li class="merkle-row" key={i}>
          <span class="merkle-level">{i + 1}</span>
          <span class="pair">
            {s.siblingSide === 'left' ? (
              <>
                <span class="node node-sibling" title={s.sibling.toHex()}>{shortHex(s.sibling, 6)}</span>
                <span class="op">‖</span>
                <span class="node node-acc" title={s.input.toHex()}>{shortHex(s.input, 6)}</span>
              </>
            ) : (
              <>
                <span class="node node-acc" title={s.input.toHex()}>{shortHex(s.input, 6)}</span>
                <span class="op">‖</span>
                <span class="node node-sibling" title={s.sibling.toHex()}>{shortHex(s.sibling, 6)}</span>
              </>
            )}
          </span>
          <span class="op">→</span>
          <span class="node node-acc" title={s.output.toHex()}>{shortHex(s.output, 6)}</span>
          <span class="muted small">sibling from proof on the {s.siblingSide}</span>
        </li>
      ))}
      <li class="merkle-row merkle-root">
        <span class="merkle-level">root</span>
        {computedRoot ? <Hash value={computedRoot} /> : <span class="muted">—</span>}
        {expectedRoot && (ok ? <Badge tone="ok">matches the tree head</Badge> : <Badge tone="err">does not match</Badge>)}
      </li>
    </ol>
  );
}

export function ConsistencyPath({ steps, seed, computedFirst, computedSecond, firstRoot, secondRoot, ok, firstRootPrepended }: { steps: ConsistencyStep[]; seed?: Uint8Array; computedFirst?: Uint8Array; computedSecond?: Uint8Array; firstRoot: Uint8Array; secondRoot: Uint8Array; ok: boolean; firstRootPrepended: boolean }) {
  return (
    <ol class="merkle">
      <li class="merkle-row">
        <span class="merkle-level">start</span>
        {seed ? <span class="node node-acc" title={seed.toHex()}>{shortHex(seed, 6)}</span> : <span class="muted">—</span>}
        <span class="muted small">{firstRootPrepended ? 'the old root itself (old size is a power of two)' : 'first proof node — the subtree where the trees diverge'}</span>
      </li>
      {steps.map((s, i) => (
        <li class="merkle-row" key={i}>
          <span class="merkle-level">{i + 1}</span>
          <span class="pair">
            {s.side === 'left' ? (
              <>
                <span class="node node-sibling" title={s.node.toHex()}>{shortHex(s.node, 6)}</span>
                <span class="op">‖</span>
                <span class="node node-acc" title={s.newInput.toHex()}>{shortHex(s.newInput, 6)}</span>
              </>
            ) : (
              <>
                <span class="node node-acc" title={s.newInput.toHex()}>{shortHex(s.newInput, 6)}</span>
                <span class="op">‖</span>
                <span class="node node-sibling" title={s.node.toHex()}>{shortHex(s.node, 6)}</span>
              </>
            )}
          </span>
          <span class="op">→</span>
          <span class="node node-acc" title={s.newOutput.toHex()}>{shortHex(s.newOutput, 6)}</span>
          {s.applied === 'both' ? <span class="muted small">also folded into the old root (old: {shortHex(s.oldOutput!, 4)})</span> : <span class="muted small">new tree only</span>}
        </li>
      ))}
      <li class="merkle-row merkle-root">
        <span class="merkle-level">old root</span>
        {computedFirst ? <Hash value={computedFirst} /> : <span class="muted">—</span>}
        {computedFirst && (computedFirst.toHex() === firstRoot.toHex() ? <Badge tone="ok">matches</Badge> : <Badge tone="err">mismatch</Badge>)}
      </li>
      <li class="merkle-row merkle-root">
        <span class="merkle-level">new root</span>
        {computedSecond ? <Hash value={computedSecond} /> : <span class="muted">—</span>}
        {computedSecond && (computedSecond.toHex() === secondRoot.toHex() ? <Badge tone="ok">matches</Badge> : <Badge tone="err">mismatch</Badge>)}
      </li>
      <li class="merkle-row">
        <span class="merkle-level" />
        {ok ? <Badge tone="ok">consistent — the new tree extends the old one</Badge> : <Badge tone="err">NOT consistent</Badge>}
      </li>
    </ol>
  );
}
